import { FC, useState, useEffect } from 'react';
import { useFragment, graphql } from 'react-relay';
import { FormattedMessage, FormattedNumber } from 'dibs-react-intl';
import { useDispatch } from 'react-redux';

import { isOfferPriceBookNameMismatch } from '../helpers/isOfferPriceBookNameMismatch';
import { getActionProps } from '../CheckoutShippingStep';
import { trackBuyerChangedCountryAndPriceBookNameMismatched } from '../../helpers/checkoutTracking';
import { ModalContainer } from 'dibs-elements/exports/ModalContainer';
import { ModalCloseButton } from 'dibs-elements/exports/ModalCloseButton';
import { BarHeader } from 'dibs-elements/exports/BarHeader';
import { ModalBody } from 'dibs-elements/exports/ModalBody';
import { Button } from 'dibs-elements/exports/Button';
import CurrencyInput from 'dibs-currency-input';
import { NumberInput } from 'dibs-elements/exports/NumberInput';
import numberConversions from 'dibs-currency-helpers/exports/util';
import { listPriceError, lowValueError } from 'dibs-buyer-layout/exports/validationErrorMessages';
import { vatMessages } from 'dibs-vat-utils/exports/vatMessages';

import {
    addressSelectedAction,
    emptyShippingAddressAction,
    transactionTypeChangeAction,
    offerAmountPerItemChangeAction,
    offerPriceBookNameChangeAction,
} from '../../actions/commonActions';
import { useCheckoutSelector } from '../../reducers/root';
import { isUserNegotiating } from '../../helpers/checkoutRedirects';

import { CheckoutShippingStep_buyer$key } from '../__generated__/CheckoutShippingStep_buyer.graphql';
import { PriceBookNameChangeModal_transaction$key } from './__generated__/PriceBookNameChangeModal_transaction.graphql';

import styles from './PriceBookNameChangeModal.scss';

const { dibsParseFloat } = numberConversions;

const percentOffMessage = (percentOff: number): JSX.Element => (
    <FormattedMessage
        id="checkout.PriceBookNameChangeModal.offListPrice"
        defaultMessage="{percentOff}% Off List Price"
        values={{
            percentOff,
        }}
    />
);

const getPercentageFromRatio = (ratio: number): number => (1 - ratio) * 100;

const transactionFragment = graphql`
    fragment PriceBookNameChangeModal_transaction on Transaction {
        priceBookName
        isAuctionOrderType
        item {
            priceBookNameList {
                priceBookName
                pricing {
                    retailPrice {
                        convertedAmountList {
                            currency
                            amount
                        }
                    }
                    isVatIncluded
                }
            }
        }
        shipment {
            shippingAddress {
                country
            }
        }
    }
`;

type Props = {
    buyer: CheckoutShippingStep_buyer$key;
    transaction: PriceBookNameChangeModal_transaction$key;
    offerPriceBookName: string | null;
    addressPriceBookName: string | null;
};

export const PriceBookNameChangeModal: FC<Props> = ({
    buyer,
    transaction: transactionRef,
    offerPriceBookName,
    addressPriceBookName,
}) => {
    const [newOfferInput, setNewOfferInput] = useState<number | null>(null);
    const [newPercentageInput, setNewPercentageInput] = useState<number | null>(null);
    const [isMakeAnOfferFlow, setIsMakeAnOfferFlow] = useState<boolean>(isUserNegotiating());
    const [previousShippingAddressIds, setPreviousShippingAddressIds] = useState<(string | null)[]>(
        []
    );
    const [previousTransactionType, setPreviousTransactionType] = useState<string | null>(null);
    const transaction = useFragment(transactionFragment, transactionRef);
    const dispatch = useDispatch();
    const transactionState = useCheckoutSelector(state => state.transaction);
    const item = transaction?.item;
    const originalPriceBook = item?.priceBookNameList?.find(
        p => p?.priceBookName === offerPriceBookName
    );
    const newPriceBook = item?.priceBookNameList?.find(
        p => p?.priceBookName === addressPriceBookName
    );
    const isAuctionOrderType = Boolean(transaction?.isAuctionOrderType);
    const country = transaction?.shipment?.shippingAddress?.country;
    const {
        transactionType,
        offerCurrency,
        shippingAddressId,
        isPrivateOffer,
        isAutomatedOffer,
        offerAmountPerItem,
    } = transactionState || {};

    // keep track of changing shippingAddressId by keeping last two ids in a list (will be used by cancel button later)
    useEffect(() => {
        setPreviousShippingAddressIds(prev => [shippingAddressId, ...prev.slice(0, 1)]);
    }, [shippingAddressId]);

    // calculate pricing information
    const originalListPrice = originalPriceBook?.pricing?.retailPrice?.convertedAmountList?.find(
        c => c?.currency === offerCurrency
    )?.amount;
    const isVatOriginal = originalPriceBook?.pricing?.isVatIncluded;
    const originalOffer = offerAmountPerItem;
    const originalOfferRatio =
        dibsParseFloat(originalOffer || 0) / dibsParseFloat(originalListPrice || 0);
    const originalPercentage = getPercentageFromRatio(originalOfferRatio);
    const newListPrice = newPriceBook?.pricing?.retailPrice?.convertedAmountList?.find(
        c => c?.currency === offerCurrency
    )?.amount;
    const isVatNew = newPriceBook?.pricing?.isVatIncluded;

    // modal eligibility
    const isMismatch = isOfferPriceBookNameMismatch(
        offerPriceBookName,
        addressPriceBookName,
        isAuctionOrderType,
        transactionType
    );

    // track mismatch
    useEffect(() => {
        if (
            isMismatch &&
            newListPrice &&
            originalListPrice &&
            addressPriceBookName &&
            offerPriceBookName
        ) {
            trackBuyerChangedCountryAndPriceBookNameMismatched({
                priceDifference: newListPrice - originalListPrice,
                newPriceBookName: addressPriceBookName,
                originalPriceBookName: offerPriceBookName,
            });
        }
    }, [isMismatch, newListPrice, originalListPrice, addressPriceBookName, offerPriceBookName]);

    if (!isMismatch || !originalOffer || !originalListPrice || !newListPrice) {
        // modal is possible only when there is a mismatch and pricing information is available
        return null;
    }

    // modal logic
    const showModal = originalListPrice !== newListPrice;
    if (transactionType !== 'NEGOTIABLE') {
        if (showModal) {
            // save current transactionType for a cancel button
            setPreviousTransactionType(transactionType);
        }

        // we are able to change priceBookName only by doing counter-offer
        dispatch(transactionTypeChangeAction({ transactionType: 'NEGOTIABLE' }));
    }
    if (transaction?.priceBookName !== addressPriceBookName) {
        // wait until transaction has correct priceBookName
        return null;
    }
    if (!showModal && addressPriceBookName) {
        // priceBookName mismatch but modal is not shown - skip modal but change priceBookName in the background
        dispatch(
            offerPriceBookNameChangeAction({
                offerPriceBookName: addressPriceBookName,
            })
        );
    }

    // prefill inputs before showing modal
    if (showModal && (newOfferInput === null || newPercentageInput === null)) {
        setNewOfferInput(
            Math.round(dibsParseFloat(newListPrice || 0) * originalOfferRatio * 100) / 100
        );
        setNewPercentageInput(originalPercentage);
    }

    // validate inputs
    let errorMessage = null;
    if (Number(newOfferInput) >= dibsParseFloat(newListPrice || 0)) {
        errorMessage = listPriceError;
    } else if ((newOfferInput && isNaN(newOfferInput)) || Number(newOfferInput) <= 0) {
        errorMessage = lowValueError;
    }

    // actions
    const resetModal = (): void => {
        setNewOfferInput(null);
        setNewPercentageInput(null);
        setPreviousTransactionType(null);
        setIsMakeAnOfferFlow(isUserNegotiating());
    };
    const onSubmit = (): void => {
        if (newOfferInput) {
            dispatch(
                offerAmountPerItemChangeAction({
                    newOffer: dibsParseFloat(newOfferInput),
                })
            );
        }
        if (addressPriceBookName) {
            dispatch(
                offerPriceBookNameChangeAction({
                    offerPriceBookName: addressPriceBookName,
                })
            );
        }
        resetModal();
    };
    const onCancel = (): void => {
        if (previousShippingAddressIds[1]) {
            // revert to original address selection
            dispatch(
                addressSelectedAction({
                    ...getActionProps(buyer, previousShippingAddressIds[1]),
                })
            );
        } else {
            // original address selection not found: revert to an "Add new address"
            dispatch(emptyShippingAddressAction());
        }

        if (previousTransactionType && previousTransactionType !== transactionType) {
            // revert to original transactionType
            dispatch(
                transactionTypeChangeAction({
                    transactionType: previousTransactionType,
                })
            );
        }

        resetModal();
    };
    const onMakeYourOffer = (): void => {
        setIsMakeAnOfferFlow(true);
    };

    return (
        <ModalContainer isOpen={showModal} onClose={onCancel} modalPosition="center">
            <ModalCloseButton onClick={onCancel} />
            <BarHeader
                title={
                    isPrivateOffer ? (
                        <FormattedMessage
                            id="checkout.PriceBookNameChangeModal.privateOfferTitle"
                            defaultMessage="An Update to Your Offer"
                        />
                    ) : (
                        <FormattedMessage
                            id="checkout.PriceBookNameChangeModal.makeAnOfferTitle"
                            defaultMessage="List Price Updated"
                        />
                    )
                }
            />

            <ModalBody>
                <p className={styles.description}>
                    {isPrivateOffer ? (
                        <>
                            <span>
                                <FormattedMessage
                                    id="checkout.PriceBookNameChangeModal.privateOfferDescription"
                                    defaultMessage="The offer price has been updated for the country you've selected."
                                />{' '}
                            </span>
                            {isAutomatedOffer ? (
                                <span>
                                    <FormattedMessage
                                        id="checkout.PriceBookNameChangeModal.privateOfferDescription2"
                                        defaultMessage="You may accept this updated offer below."
                                    />
                                </span>
                            ) : (
                                <span>
                                    <FormattedMessage
                                        id="checkout.PriceBookNameChangeModal.privateOfferDescription3"
                                        defaultMessage="You may accept this updated offer or submit a counteroffer."
                                    />
                                </span>
                            )}
                        </>
                    ) : (
                        <span>
                            <FormattedMessage
                                id="checkout.PriceBookNameChangeModal.makeAnOfferDescription"
                                defaultMessage="The list price has been updated for the country you've selected. Please
                                revise your offer."
                            />
                        </span>
                    )}
                </p>
                <hr className={styles.hr} />
                <div className={styles.flex}>
                    {isPrivateOffer ? (
                        <>
                            <span>
                                <FormattedMessage
                                    id="checkout.PriceBookNameChangeModal.originalOffer"
                                    defaultMessage="Original offer"
                                />

                                <br />
                                <span className={styles.muted}>
                                    {percentOffMessage(Math.round(originalPercentage || 0))}
                                </span>
                            </span>
                            <span className={styles.alignRight}>
                                <span>
                                    <span className={styles.strikethrough}>
                                        <FormattedNumber
                                            value={originalListPrice}
                                            currency={offerCurrency}
                                            style="currency"
                                        />
                                    </span>{' '}
                                    <FormattedNumber
                                        value={originalOffer}
                                        currency={offerCurrency}
                                        style="currency"
                                    />
                                </span>
                                <br />
                                {isVatOriginal &&
                                    vatMessages.included({ priceBookName: offerPriceBookName })}
                            </span>
                        </>
                    ) : (
                        <>
                            <span>
                                <FormattedMessage
                                    id="checkout.PriceBookNameChangeModal.originalListPrice"
                                    defaultMessage="Original list price"
                                />
                            </span>
                            <span className={styles.alignRight}>
                                <span>
                                    <FormattedNumber
                                        value={originalListPrice}
                                        currency={offerCurrency}
                                        style="currency"
                                    />
                                </span>
                                <br />
                                {isVatOriginal &&
                                    vatMessages.included({ priceBookName: offerPriceBookName })}
                            </span>
                        </>
                    )}
                </div>
                <hr className={styles.hr} />
                <div className={styles.flex}>
                    {isMakeAnOfferFlow ? (
                        <>
                            <span className={styles.heavyFont}>
                                <FormattedMessage
                                    id="checkout.PriceBookNameChangeModal.listPriceFor"
                                    defaultMessage="List Price for {country}"
                                    values={{ country }}
                                />
                            </span>
                            <span className={styles.alignRight}>
                                <span className={styles.heavyFont}>
                                    <FormattedNumber
                                        value={newListPrice}
                                        currency={offerCurrency}
                                        style="currency"
                                    />
                                </span>
                                <br />
                                {isVatNew &&
                                    vatMessages.included({ priceBookName: addressPriceBookName })}
                            </span>
                        </>
                    ) : (
                        <>
                            <span className={styles.heavyFont}>
                                <FormattedMessage
                                    id="checkout.PriceBookNameChangeModal.yourOfferFor"
                                    defaultMessage="Your Offer for {country}"
                                    values={{ country }}
                                />
                                <br />
                                <span className={styles.muted}>
                                    {percentOffMessage(Math.round(newPercentageInput || 0))}
                                </span>
                            </span>
                            <span className={styles.alignRight}>
                                <span className={styles.heavyFont}>
                                    <span className={styles.strikethrough}>
                                        <FormattedNumber
                                            value={newListPrice}
                                            currency={offerCurrency}
                                            style="currency"
                                        />
                                    </span>{' '}
                                    <FormattedNumber
                                        value={Number(newOfferInput)}
                                        currency={offerCurrency}
                                        style="currency"
                                    />
                                </span>
                                <br />
                                {isVatNew &&
                                    vatMessages.included({ priceBookName: addressPriceBookName })}
                            </span>
                        </>
                    )}
                </div>
                {isMakeAnOfferFlow && (
                    <>
                        <div className={styles.inputWrapper}>
                            <div className={styles.flex}>
                                <CurrencyInput
                                    label={
                                        <FormattedMessage
                                            id="checkout.PriceBookNameChangeModal.yourOffer"
                                            defaultMessage="Your Offer"
                                        />
                                    }
                                    currency={offerCurrency}
                                    dataTn="price-book-name-change-your-offer-input"
                                    errorMessage={errorMessage}
                                    errorMessageAlignment="left"
                                    name="price-book-name-change-your-offer-input"
                                    onChange={val => {
                                        let newNewPercentage = getPercentageFromRatio(
                                            dibsParseFloat(val || 0) /
                                                dibsParseFloat(newListPrice || 0)
                                        );
                                        if (newNewPercentage < 0) {
                                            newNewPercentage = 0;
                                        }
                                        setNewOfferInput(Number(val));
                                        setNewPercentageInput(newNewPercentage);
                                    }}
                                    value={newOfferInput}
                                />
                                <span className={styles.or}>or</span>
                                <div className={styles.percentOff}>
                                    <NumberInput
                                        label={
                                            <FormattedMessage
                                                id="checkout.PriceBookNameChangeModal.percentOff"
                                                defaultMessage="Percent Off"
                                            />
                                        }
                                        onChange={val => {
                                            if (!val || val < 0) {
                                                val = 0;
                                            } else if (val > 100) {
                                                val = 100;
                                            }
                                            setNewOfferInput(
                                                (1 - val / 100) * dibsParseFloat(newListPrice || 0)
                                            );
                                            setNewPercentageInput(val);
                                        }}
                                        name="price-book-name-change-percent-off-input"
                                        dataTn="price-book-name-change-percent-off-input"
                                        rightDecorator="%"
                                        value={newPercentageInput}
                                    />
                                </div>
                            </div>
                            {isVatNew && (
                                <p className={styles.offerIncludesVat}>
                                    {vatMessages.yourOfferIncludes({
                                        priceBookName: addressPriceBookName,
                                    })}
                                </p>
                            )}
                        </div>
                    </>
                )}

                <Button
                    className={styles.submitButton}
                    disabled={!!errorMessage}
                    onClick={onSubmit}
                    fullWidth
                >
                    {isMakeAnOfferFlow ? (
                        <FormattedMessage
                            id="checkout.PriceBookNameChangeModal.continueWithUpdatedOffer"
                            defaultMessage="Continue with updated offer"
                        />
                    ) : (
                        <FormattedMessage
                            id="checkout.PriceBookNameChangeModal.acceptUpdatedOffer"
                            defaultMessage="Accept updated offer"
                        />
                    )}
                </Button>

                {isMakeAnOfferFlow || isAutomatedOffer ? (
                    <Button className={styles.button} type="secondary" onClick={onCancel} fullWidth>
                        <FormattedMessage
                            id="checkout.PriceBookNameChangeModal.cancel"
                            defaultMessage="Cancel"
                        />
                    </Button>
                ) : (
                    <Button
                        className={styles.button}
                        type="secondary"
                        onClick={onMakeYourOffer}
                        fullWidth
                    >
                        <FormattedMessage
                            id="checkout.PriceBookNameChangeModal.makeYourOffer"
                            defaultMessage="Make your offer"
                        />
                    </Button>
                )}
            </ModalBody>
        </ModalContainer>
    );
};
